// src/lib/accuracy-decay.ts
//
// Recency-weighted accuracy: a single source of truth for HOW MUCH a
// past attempt counts toward "your accuracy on this topic" today.
//
// Why we don't use raw averaging:
// A user grinding hard for two weeks should see their topic accuracy
// MOVE in response to that effort. Raw flat averaging treats a 6-month-
// old wrong answer the same as today's correct answer, which makes
// progress hard to see — especially on heavily-attempted topics.
//
// What we use instead:
// Exponential decay with a 30-day half-life. Each attempt's weight is
//
//     weight = 0.5 ^ (days_since_attempt / HALF_LIFE_DAYS)
//
// So:
//     today  → 1.00
//     7 days → 0.85
//    30 days → 0.50
//    60 days → 0.25
//    90 days → 0.125
//
// 30 days is a balance: recent month dominates new practice without
// erasing the past entirely. Longer half-lives feel unresponsive
// (a week of grinding doesn't move the needle); shorter ones make
// accuracy too jumpy and erase mastery achieved further back.
//
// The DB query (in src/app/study-plan/page.tsx) computes the weighted
// sums in Postgres so we don't have to pull every AttemptItem row over
// the wire just to apply the decay. The math here exists for unit tests
// and for any client-side weighting we may add later.

/**
 * Half-life of an attempt's contribution to recency-weighted accuracy,
 * measured in days. Confirmed with product 2026-04-30.
 *
 * Tunable: shorter values make accuracy more responsive but noisier;
 * longer values make it more stable but obscure recent improvement.
 */
export const HALF_LIFE_DAYS = 30

/**
 * Compute the decay weight of an attempt that happened `daysAgo` days
 * before "now". Returns a number in [0, 1] — exactly 1.0 when today,
 * exactly 0.5 at the half-life boundary, asymptotically approaching 0
 * for very old attempts.
 *
 * Negative `daysAgo` (i.e. future-dated attempts, shouldn't happen
 * but defensive) is clamped to 1.0 so a stray clock skew doesn't
 * produce weights > 1.
 */
export function decayWeight(daysAgo: number, halfLifeDays: number = HALF_LIFE_DAYS): number {
  if (daysAgo <= 0) return 1.0
  return Math.pow(0.5, daysAgo / halfLifeDays)
}

/**
 * Compute the recency-weighted accuracy percentage from a list of
 * attempts, each with a daysAgo offset and a correct flag.
 *
 * Used for unit tests + as a reference implementation that the SQL
 * query is expected to mirror. NOT called from production server code
 * (which lets Postgres do the math directly to avoid pulling every
 * AttemptItem row).
 *
 * Returns null when the input is empty (caller decides how to render).
 */
export function recencyWeightedAccuracy(
  attempts: Array<{ daysAgo: number; correct: boolean }>,
  halfLifeDays: number = HALF_LIFE_DAYS,
): number | null {
  if (attempts.length === 0) return null
  let weightedCorrect = 0
  let weightedTotal = 0
  for (const a of attempts) {
    const w = decayWeight(a.daysAgo, halfLifeDays)
    weightedTotal += w
    if (a.correct) weightedCorrect += w
  }
  if (weightedTotal === 0) return null
  return weightedCorrect / weightedTotal
}

// SQL-fragment helpers used to live here (weightedCorrectSqlFragment /
// weightedTotalSqlFragment). They were never wired into production code —
// the SQL is inlined in src/app/study-plan/page.tsx — and the fragment
// strings encoded a `.0` suffix on the half-life that's a footgun if
// ever embedded via Prisma's tagged-template `$queryRaw` (it produces
// `$N.0`, which Postgres rejects as a syntax error). See the production
// hotfix at d77b0ca for the full incident.
//
// If a future caller needs SQL fragments here, generate them via
// `Prisma.raw()` (literal SQL injection — only safe for trusted/constant
// input) so the half-life lands as a literal number in the query, not
// as a parameter placeholder.
